import { useEffect, useState } from "react";
import { Input, trim } from "../../../../../components";
import { EditorInput } from "../../../../../components/input/EditorInput";

interface Props {
  role: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  isEditable: boolean;
  onHeaderContentUpdate?: (
    title: string,
    name: string | undefined,
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  onDatesClick?: () => void;
  isProject?: boolean;
  isDefaultLead?: boolean;
  /** When false, dates stay read-only even in edit mode (default true). */
  datesEditable?: boolean;
}

export const WorkplaceHeader = ({
  role,
  name,
  startDate,
  endDate,
  isEditable,
  onHeaderContentUpdate,
  onDatesClick,
  isProject,
  isDefaultLead,
  datesEditable = true,
}: Props) => {
  const [localRole, setLocalRole] = useState(role ?? "");
  const [localName, setLocalName] = useState(name ?? "");

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr || dateStr === "" || dateStr === "End Year") {
      return "";
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Check if it's YYYY-MM format
    if (dateStr.includes("-")) {
      const [year, month] = dateStr.split("-");
      const monthIndex = parseInt(month, 10) - 1; // Convert to 0-based index
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]} ${year}`;
      }
    } else {
      // YYYY format - just return the year
      return dateStr;
    }

    return dateStr; // Fallback to original string if parsing fails
  };

  const getDateText = () => {
    if (role === "" && name === "" && startDate === "" && endDate === "") {
      return "";
    }

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    if (startDate !== "" && endDate !== "") {
      return `${formattedStartDate} - ${endDate === "End Year" ? "current" : formattedEndDate}`;
    }
    if (startDate !== "" && endDate === "") {
      return `${formattedStartDate} - current`;
    }
    if (startDate === "" && endDate === "") {
      return `current`;
    }

    return "current";
  };

  return (
    <div className="flex flex-col">
      {isEditable ? (
        <EditorInput
          label="Your role"
          transformValue={trim}
          value={localRole}
          disabled={isDefaultLead}
          onBlur={(
            event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const inputValue = event.target.value;
            setLocalRole(inputValue);
            if (onHeaderContentUpdate) {
              onHeaderContentUpdate(inputValue, name, startDate, endDate);
            }
          }}
          onChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            setLocalRole(event.target.value);
          }}
        />
      ) : (
        <p className="font-semibold">{role}</p>
      )}

      <div className="flex flex-row justify-between w-full">
        {isEditable ? (
          <EditorInput
            label="Workplace name"
            transformValue={trim}
            value={localName}
            disabled={isDefaultLead}
            onBlur={(
              event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              const inputValue = event.target.value;
              setLocalName(inputValue);
              if (onHeaderContentUpdate) {
                onHeaderContentUpdate(
                  localRole,
                  inputValue,
                  startDate,
                  endDate,
                );
              }
            }}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              setLocalName(event.target.value);
            }}
            className="text-gray-6 text-sm"
          />
        ) : (
          name && <p className="text-gray-6 text-sm font-medium">{name}</p>
        )}

        {isProject ? null : isEditable && datesEditable ? (
          <EditorInput
            label="Date"
            transformValue={trim}
            onClick={() => {
              if (onDatesClick) {
                onDatesClick();
              }
            }}
            value={getDateText()}
            onBlur={(
              event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {}}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {}}
            className="text-gray-6 text-sm"
            inputClassName="text-right"
          />
        ) : (
          <p className="text-gray-6 text-sm text-right font-medium whitespace-nowrap shrink-0 pl-2">
            {getDateText()}
          </p>
        )}
      </div>
    </div>
  );
};
